/**
 * Utility for managing console output to prevent excessive logging
 */

type LogCache = {
    [key: string]: {
        lastMessage: string;
        count: number;
        lastTime: number;
    };
};

const logCache: LogCache = {};
const LOG_THROTTLE_MS = 5000; // Only log once per 5 seconds for the same message

/**
 * Throttled logging to prevent console flooding
 * @param key Unique identifier for this type of log
 * @param message The message to log
 * @param data Optional data to include with the log
 */
export function throttledLog(key: string, message: string, data?: any): void {
    const now = Date.now();
    const cache = logCache[key];

    // First occurrence or different message
    if (!cache || cache.lastMessage !== message) {
        console.log(`[${key}] ${message}`, data || "");
        logCache[key] = {
            lastMessage: message,
            count: 1,
            lastTime: now
        };
        return;
    }

    // Update count
    cache.count++;

    // If enough time has passed, log again with count
    if (now - cache.lastTime > LOG_THROTTLE_MS) {
        console.log(`[${key}] ${message} (${cache.count} occurrences)`, data || "");
        cache.count = 0;
        cache.lastTime = now;
    }
}

/**
 * Debug logger that only shows a limited number of messages
 */
let debugCount = 0;
const DEBUG_LIMIT = 10; // Only log first 10 occurrences

export function limitedLog(message: string, data?: any, forceShow = false): void {
    if (forceShow || debugCount < DEBUG_LIMIT) {
        console.log(`[DEBUG] ${message}`, data || "");
        debugCount++;

        if (debugCount === DEBUG_LIMIT) {
            console.log("[DEBUG] Further logs suppressed to avoid console flooding");
        }
    }
}

/**
 * Reset debug counter (useful for development)
 */
export function resetLogLimits(): void {
    debugCount = 0;
    console.log("[DEBUG] Log limits have been reset");
}
